import { useState, useCallback } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { TEST_SFTP_CONNECTION, IMPORT_FILE_FROM_SFTP } from '../../../mutations/fileMutations';
import { FILES_QUERY } from '../../../queries';
import { SET_SUCCESS, SET_LOADING } from '../../../reducers/actionReducer';
import ErrorText from '../../ErrorText';
import LoadingSpinner from '../../LoadingSpinner';
import Button from '../../inputs/Button';
import CollapsibleInstructions from '../CollapsibleInstructions';

interface SftpConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  remotePath: string;
  filename?: string;
}

interface SftpImportProps {
  dispatchAction: (action: any) => void;
}

export function SftpImport({ dispatchAction }: SftpImportProps) {
  const [sftpConfig, setSftpConfig] = useState<SftpConfig>({
    host: '',
    port: 22,
    username: '',
    password: '',
    remotePath: '',
    filename: '',
  });
  
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionValid, setConnectionValid] = useState(false);
  const [importing, setImporting] = useState(false);

  const apolloClient = useApolloClient();

  const [testConnection, { loading: testingConnection, error: connectionError }] = useMutation(
    TEST_SFTP_CONNECTION
  );

  const [importFile, { loading: importingFile, error: importError }] = useMutation(
    IMPORT_FILE_FROM_SFTP
  );

  const handleInputChange = useCallback((field: keyof SftpConfig, value: string | number) => {
    setSftpConfig(prev => ({
      ...prev,
      [field]: value,
    }));
    // Reset connection status when config changes
    if (connectionTested) {
      setConnectionTested(false);
      setConnectionValid(false);
    }
  }, [connectionTested]);

  const handleTestConnection = useCallback(async () => {
    try {
      const result = await testConnection({
        variables: {
          sftpConfig: {
            host: sftpConfig.host,
            port: sftpConfig.port,
            username: sftpConfig.username,
            password: sftpConfig.password,
            remotePath: sftpConfig.remotePath,
          },
        },
      });

      if (result.data?.testSftpConnection?.success) {
        setConnectionTested(true);
        setConnectionValid(true);
      }
    } catch (error) {
      setConnectionTested(true);
      setConnectionValid(false);
      console.error('Connection test failed:', error);
    }
  }, [sftpConfig, testConnection]);

  const handleImportFile = useCallback(async () => {
    if (!connectionValid) {
      return;
    }

    setImporting(true);
    dispatchAction({
      type: SET_LOADING,
      payload: {
        message: 'Importing file from SFTP server...',
      },
    });

    try {
      const result = await importFile({
        variables: {
          sftpConfig: {
            host: sftpConfig.host,
            port: sftpConfig.port,
            username: sftpConfig.username,
            password: sftpConfig.password,
            remotePath: sftpConfig.remotePath,
            filename: sftpConfig.filename || undefined,
          },
        },
      });

      if (result.data?.importFileFromSftp?.success) {
        dispatchAction({
          type: SET_SUCCESS,
          payload: {
            message: result.data.importFileFromSftp.message,
          },
        });

        // Refresh files query
        apolloClient.refetchQueries({
          include: [FILES_QUERY],
        });
      }
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      setImporting(false);
    }
  }, [sftpConfig, connectionValid, importFile, dispatchAction, apolloClient]);

  const isFormValid = sftpConfig.host && sftpConfig.username && sftpConfig.password && sftpConfig.remotePath;
  const canTestConnection = isFormValid && !testingConnection;
  const canImport = connectionValid && !importing && !importingFile;

  return (
    <div className="w-full flex flex-col gap-6 p-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2">Import File from SFTP Server</h3>
        <p className="text-gray-600 text-sm">
          Enter your SFTP server details to import a CSV file
        </p>
      </div>

      <div className="space-y-5 max-w-md mx-auto w-full">
        {/* Host */}
        <div>
          <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-1">
            Server Address *
          </label>
          <input
            id="host"
            type="text"
            value={sftpConfig.host}
            onChange={(e) => handleInputChange('host', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="sftp.example.com"
          />
        </div>

        {/* Port */}
        <div>
          <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-1">
            Port
          </label>
          <input
            id="port"
            type="number"
            value={sftpConfig.port}
            onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 22)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="22"
          />
        </div>

        {/* Username */}
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
            Username *
          </label>
          <input
            id="username"
            type="text"
            value={sftpConfig.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="your-username"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password *
          </label>
          <input
            id="password"
            type="password"
            value={sftpConfig.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="your-password"
          />
        </div>

        {/* Remote Path */}
        <div>
          <label htmlFor="remotePath" className="block text-sm font-medium text-gray-700 mb-1">
            File Path *
          </label>
          <input
            id="remotePath"
            type="text"
            value={sftpConfig.remotePath}
            onChange={(e) => handleInputChange('remotePath', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="/path/to/your/file.csv"
          />
        </div>

        {/* Filename (optional) */}
        <div>
          <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-1">
            Local Filename (optional)
          </label>
          <input
            id="filename"
            type="text"
            value={sftpConfig.filename}
            onChange={(e) => handleInputChange('filename', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#14b8a6] focus:border-[#14b8a6]"
            placeholder="Leave empty to use original filename"
          />
        </div>
      </div>

      {/* Connection Status */}
      {connectionTested && (
        <div className={`p-3 rounded-md ${connectionValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <p className={`text-sm ${connectionValid ? 'text-green-700' : 'text-red-700'}`}>
            {connectionValid ? '✅ Connection successful!' : '❌ Connection failed'}
          </p>
        </div>
      )}

      {/* Errors */}
      {connectionError && (
        <ErrorText message={connectionError.message} />
      )}
      {importError && (
        <ErrorText message={importError.message} />
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 max-w-md mx-auto w-full">
        <button
          type="button"
          className="w-full bg-[#14b8a6] text-white font-medium py-2.5 rounded-md hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          onClick={handleImportFile}
          disabled={!canImport}
        >
          {importing || importingFile ? (
            <>
              <LoadingSpinner className="w-4 h-4 mr-2 inline" />
              Connecting & Importing...
            </>
          ) : (
            'Connect & Import'
          )}
        </button>
        
        <Button
          type="button"
          secondary
          onClick={handleTestConnection}
          disabled={!canTestConnection}
          className="w-full"
        >
          {testingConnection ? (
            <>
              <LoadingSpinner className="w-4 h-4 mr-2" />
              Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
      </div>

      <div className="text-xs text-gray-500 mt-2">
        * Required fields
      </div>

      <CollapsibleInstructions className="mt-6" />
    </div>
  );
}

export default SftpImport;
